import http from '@/biz/http.mjs'
import buildUrl from '@/script/buildUrl.mjs'
import openWindow from '@/script/openWindow.mjs'
import {toProductMap} from '../../compatibility.mjs'
import apiSnapshot from '../../apis/apiSnapshot.mjs'
import parseMap from '../../scripts/parseMap.mjs'
import extendNode from '../extendNode.mjs'
import useDesignNode from '../_DESIGN/useDesignNode.jsx'
import meta from './metaProductNode.mjs'

/**
 * 制品节点类型的基类
 */
export default () => {
    const DesignNode = useDesignNode()

    return extendNode(DesignNode, {
        ...meta,

        get create() {
            const {create} = this.api

            if (! create) {
                return void 0
            }

            return async (map, node) => {
                const data = this.getCreateData(map, node)
                const {prjId, subTypeCode, sVer} = map.data

                const {
                    [this.mapProp]: _,
                    ...updates
                } = await create({...data, prjId, subTypeCode, sVer})

                return {
                    ...updates,
                    stsCode: 'DRAFT',
                    stsName: '草稿',
                }
            }
        },

        get discard() {
            const {discard} = this.api

            if (! discard) {
                return void 0
            }

            return async (map, node) => {
                const {
                    [this.mapProp]: _,
                    ...updates
                } = await discard(node.data)

                return updates
            }
        },

        get publish() {
            const {publish} = this.api

            if (! publish) {
                return void 0
            }

            return async (map, node, extra) => {
                const {pkid, prjId, sVer} = node.data
                const result = await publish({...extra, pkid, prjId, sVer})
                const {dppChangeList, ...updates} = result
                node.data = {...node.data, ...updates}

                map.data = {
                    ...map.data,
                    ...this._getMetaUpdates(node.data),
                }

                this.clean(map, node)

                if (! dppChangeList) {
                    return
                }
                const componentUpdates = new Map(
                    dppChangeList.map(({pkid, rev}) => [pkid, rev])
                )

                for (const n of map.dfs(node)) {
                    const {pkid} = n.data

                    if (! pkid) {
                        continue
                    }

                    const rev = componentUpdates.get(pkid)

                    if (rev) {
                        n.data = {...n.data, lastRev: rev, rev}
                    }
                }
            }
        },

        get pull() {
            const {pull} = this.api

            if (! pull) {
                return void 0
            }

            return async (map, node) => {
                const {
                    [this.mapProp]: _,
                    ...tree
                } = await pull(node.data)

                await this._pull(map, node, parseMap(tree))
            }
        },

        get push() {
            const {push} = this.api

            if (! push) {
                return void 0
            }

            return async (map, node) => {
                const args = await this._getPushData(map, node)
                const result = await push(args)
                await this._pushDone(map, node, result)
            }
        },

        get readTree() {
            const {read} = this.api

            if (! read) {
                return void 0
            }

            return async args => {
                if (args.rev) {
                    const snapshot = await apiSnapshot.readByRev(
                        {...args, bizNodeType: this.type}
                    )

                    const {root} = parseMap(snapshot)
                    const {prjNo} = snapshot.data
                    Object.assign(root.data, {prjNo})
                    return root
                }
                else {
                    const {
                        [this.mapProp]: mapJson,
                        ...data
                    } = await read(args)

                    const tree = {
                        data: {
                            ...data,
                            bizNodeType: this.type,
                        },

                        children: [],
                    }

                    if (mapJson) {
                        const {root: {children}} = parseMap(mapJson)
                        Object.assign(tree, {children})
                    }

                    return tree
                }
            }
        },

        get readMap() {
            const {read} = this.api

            if (! (read && this.mapProp)) {
                return void 0
            }

            return async args => {
                const {
                    [this.mapProp]: mapJson,
                    ...rootData
                } = await read(args)

                const mapData = parseMap(mapJson)
                Object.assign(mapData.root.data, rootData)

                mapData.data = {
                    ...mapData.data,
                    ...this._getMetaUpdates(rootData),
                }

                return mapData
            }
        },

        get revise() {
            const {revise} = this.api

            if (! revise) {
                return void 0
            }

            return async (map, node) => {
                const {
                    [this.mapProp]: mapModel,
                    ...updates
                } = await revise(node.data)

                node.data = {...node.data, ...updates}

                map.data = {
                    ...map.data,
                    ...this._getMetaUpdates(node.data),
                }

                const {root} = parseMap(mapModel)
                const _n = map.nodeProxy(node)
                await _n.replace(root)
            }
        },

        get update() {
            const {update} = this.api

            if (! update) {
                return void 0
            }

            return async (map, node) => {
                const {
                    [this.mapProp]: _,
                    ...updates
                } = await update(node.data)

                node.data = {...node.data, ...updates}
            }
        },

        get updateMap() {
            const {update, updateMap} = this.api
            const doUpdate = updateMap ?? update

            if (! doUpdate) {
                return void 0
            }

            return async (map, node) => {
                const mapName = this.getText(map, node)
                const root = this.exportTree(map, node, {compact: true})

                const mapData = {
                    data: {...map.data, mapName},
                    root,
                }

                const json = JSON.stringify(toProductMap(mapData))

                // 接口某些字段返回了 null，不能用返回值去更新现有数据
                await doUpdate({
                    ...node.data,
                    mapSize: json.length,
                    nodeQty: map.descendantCount.getCount(node) + 1,
                    [this.mapProp]: json,
                })
            }
        },

        get undoRevise() {
            const {publish} = this.api

            if (! publish) {
                return void 0
            }

            return async (map, node) => {
                const {pkid, prjId, sVer} = node.data

                const {dataMap} = await http.get(
                    '/basmapvers/latestVer',
                    {dataId: pkid, prjId, sVer}
                )

                const {data, root} = parseMap(dataMap)
                this.data = data
                this.replace(map, node, root)

                await this.updateMap(map, node)
                await this.push(map, node)
                await this.publish(map, node)
            }
        },

        canChangeVersion(map, node) {
            if (! node.parent) {
                map.logger.error('根节点不能改变版本', [node])
                return false
            }

            if (! DesignNode.canChangeVersion.call(this, map, node)) {
                return false
            }

            const _p = map.nodeProxy(node.parent)
            return _p.canWriteTree()
        },

        canGrow(map, node) {
            if (! DesignNode.canGrow.call(this, map, node)) {
                return false
            }

            if (! this.readTree) {
                const name = this.typeName(map, node.parent)
                map.logger.error(`${name}不支持加载/卸载`, [node])
                return false
            }

            return true
        },

        canWriteData(map, node) {
            const {mapTypeCode} = map.data
            const {bizNodeType} = node.data

            return (
                (
                    ! node.data.pkid ||

                    (
                        ! node.parent &&
                        `_${bizNodeType}_MAP` === mapTypeCode
                    )
                ) &&

                DesignNode.canWriteData.call(this, map, node)
            )
        },

        canWriteTree(map, node) {
            return (
                node === map.root &&
                DesignNode.canWriteTree.call(this, map, node)
            )
        },

        getCreateData(map, node) {
            this._validate(map, node)
            return {id: node.id, ...node.data}
        },

        getStyle(map, node) {
            const shape = this._getShape(map, node)
            const backgroundColor = this._getThemeColor(map, node)

            return {
                ...this._getStyle(map, node, {backgroundColor}),
                shape,
            }
        },

        getText(map, node) {
            const text = DesignNode.getText.call(this, map, node)

            if (
                'REVISE' === node.data.stsCode &&
                this.isLinked(map, node)
            ) {
                return text + '*'
            }
            else {
                return text
            }
        },

        getUrl(map, node, childPath = []) {
            const {
                [this.textProp]: text = '',
                pkid,
            } = node.data

            if (pkid) {
                const q = this._getUrlQuery(node)

                if (0 < childPath.length) {
                    const path = [encodeURIComponent(text), ...childPath]

                    q.mapInits = {
                        selectedNodes: [{
                            path: `/${path.join('/')}/`,
                        }],
                    }
                }

                return buildUrl(this.detailUrl, q)
            }
            else {
                return DesignNode.getUrl.call(this, map, node)
            }
        },

        async gotoCurrent(map, node, target) {
            const {patternRev, prjPatternId} = map.data
            const {pkid} = node.data

            openWindow(
                this.detailUrl,

                {
                    patternRev,
                    prjPatternId,
                    [this.detailIdProp]: pkid,
                },

                target
            )
        },

        async gotoDiff(map, node, leftRev, rightRev, target) {
            const {bizNodeType} = node.data

            const [basMapVerId, tarMapVerId] = await Promise.all([
                this._readSnapshotId(map, node, leftRev),
                this._readSnapshotId(map, node, rightRev),
            ])

            openWindow(
                '/DiffMap',
                {basMapVerId, tarMapVerId, type: bizNodeType},
                target
            )
        },

        async gotoRev(map, node, rev, target) {
            const {bizNodeType, pkid} = node.data

            const id = await apiSnapshot.fetchIdByRev(
                {bizNodeType, pkid, rev}
            )

            const {patternRev, prjPatternId} = map.data

            openWindow(
                '/SoftwareMap',

                {
                    id,
                    mode: 'snapshot',
                    patternRev,
                    prjPatternId,
                },

                target
            )
        },

        async onDoubleClick(map, node, event) {
            const target = event.altKey ? '_self' : '_blank'

            const {
                basRev,
                deliverRev,
                deliverVer,
                lastRev,
                pkid,
                snapshotId,
                sVer,
            } = node.data

            if (! pkid) {
                await DesignNode.onDoubleClick.call(this, map, node, event)
            }
            else if (node.parent) {
                event.preventDefault()
                const rev = this.getRev(map, node)

                if (/^_(CHG|DES)_MAP$/.test(map.data.mapTypeCode)) {
                    if (basRev && basRev !== rev) {
                        await this.gotoDiff(map, node, rev, basRev, target)
                    }
                    else if (
                        deliverRev &&
                        deliverVer &&

                        (
                            deliverRev !== rev ||
                            deliverVer !== sVer
                        )
                    ) {
                        await this.gotoDiff(map, node, rev, deliverRev, target)
                    }
                }
                //else if (null !== rev && rev < lastRev) {
                else if (rev) {
                    await this.gotoRev(map, node, rev, target)
                }
                else {
                    await this.gotoCurrent(map, node, target)
                }
            }
            else if (snapshotId) {
                openWindow(
                    '/SoftwareMap',
                    {id: snapshotId, mode: 'snapshot'},
                    target
                )
            }
            else if (
                '' === snapshotId ||
                /^(ANALYSE|DEPEND|EFFECT|IS)_MAP$/.test(map.data.mapTypeCode)
            ) {
                await this.gotoCurrent(map, node, target)
            }
            else {
                DesignNode.onDoubleClick.call(this, map, node, event)
            }
        },

        showVersion(map, node) {
            const {mapTypeCode} = map.data
            const {bizNodeType} = node.data

            return (
                DesignNode.showVersion.call(this, map, node) &&

                (
                    'ANALYSE_MAP' === mapTypeCode ||
                    `_${bizNodeType}_MAP` !== mapTypeCode ||
                    node.parent
                )
            )
        },

        //async upgrade(map, node) {
            //const {deliverRev, deliverVer, pkid} = node.data
            //const tree = await this.readTree({pkid})
            //const {rev, stsCode} = tree.data

            //if (! /^(RLS|REVISE)$/.test(stsCode)) {
                //throw new Error('制品不是发布/修订状态，不能升级')
            //}

            //node.data = {
                //...tree.data,
                //deliverRev,
                //deliverVer,
                //lastRev: rev,
                //rev
            //}

            //if (node.firstChild) {
                //await this.grow(map, node)
            //}
        //},

        _getMetaUpdates(rootData) {
            const {
                crtId,
                crtIp,
                crtName,
                crtTime,
                pkid,
                rev: mapRev,
                rlsTime,
                rlsUserId,
                rlsUserName,
                stsCode: mapStsCode,
                stsName: mapStsName,
                sVer: mapVer,
                uptId,
                uptIp,
                uptName,
                uptTime,
            } = rootData

            return {
                crtId,
                crtIp,
                crtName,
                crtTime,
                mapRev,
                mapStsCode,
                mapStsName,
                pkid,
                rlsTime,
                rlsUserId,
                rlsUserName,
                sVer: mapVer,
                uptId,
                uptIp,
                uptName,
                uptTime,
            }
        },

        _getShape(map, node) {
            return 'RoundedRectangle'
        },

        _getThemeColor(map, node) {
            return '#fff'
        },

        _getUrlQuery(node) {
            return {[this.detailIdProp]: node.data.pkid}
        },

        async _onAttach(map, node, event) {
            if (event.target === node) {
                if (this.isLinked(map, node)) {
                    if (
                        // 没有 rev 字段视为版本 0
                        ! node.data.rev &&
                        // 在自己的逻辑图里允许粘贴
                        map.root.data.pkid !== node.data.pkid
                    ) {
                        event.stopPropagation()
                        map.deleteTree(node)
                        map.logger.error('不能粘贴版本为0的模件', [node])
                        return
                    }
                }
            }

            await DesignNode._onAttach.call(this, map, node, event)
        },

        async _pull(map, node, tree) {
            await this.replace(map, node, tree)
            await this.emitEventDown(map, node, {type: 'pull'})
        },

        async _readGrowTree(map, node) {
            const {pkid} = node.data
            const rev = this.getRev(map, node)
            return this.readTree({pkid, rev})
        },

        async _readSnapshotId(map, node, rev) {
            const {bizNodeType, pkid} = node.data

            const {pkid: id} = await http.get(
                '/basmapvers/verid',
                {bizNodeType, pkid, rev}
            )

            return id
        },
    })
}
